import React, {CSSProperties, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useRecoilValue} from 'recoil';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import * as AppState from '../../AppState';
import {LocationState, Property} from '../../interfaces/interfaces';
import smoothscroll from 'smoothscroll-polyfill';
import classNames from 'classnames';
import {AppList, baseUrl, baseRealEstateUrl, Footer, getUrlWithUtmQueryString} from '@capcenter/shared';
import numeral from 'numeral';
import {buildPropertyDetailsUrl, isCapCenterListOffice, toTitleCase} from '../../utilities/util';
import GoogleStaticMapSrc from '../Shared/GoogleStaticMapSrc';
import ImageGallery from './ImageGallery/ImageGallery';
import FullScreenGallery from './ImageGallery/FullScreenGallery';
import ExpansionComponent from './ExpansionComponent/ExpansionComponent';
import PropertyDetailsSkeleton from './PropertyDetailsSkeleton';

// UI COMPONENTS
import DetailsHighlight from './UIComponents/DetailsHighlight';
import PropertyDetailsHeader from './UIComponents/PropertyDetailsHeader';
import ImportantDetails from './UIComponents/ImportantDetails';
import PropertyDescription from './UIComponents/PropertyDescription';
import PropertyLocation from './UIComponents/PropertyLocation';
import MlsDisclosures from './UIComponents/MlsDisclosures';

// DATA GROUP COMPONENTS
import ListingDetails from './DataGroupComponents/ListingDetails';
import FullDetails from './DataGroupComponents/FullDetails';

import {getPropertyAddressFromUrl, getPropertyIdFromUrl} from '../../services/routingUrls';
import {fetchSinglePropertyViaAddress, useGetProperties} from '../../services/search/useGetPropertiesCH';
import {getMatchedCountyObject} from '../../services/getCounties';

import './PropertyDetails.scss';
import {BreadCrumb} from './BreadCrumb/BreadCrumb';
import SavingsHighlight from './UIComponents/SavingsHighlight';
import CallsToActionHighlight from './UIComponents/CallsToActionHighlight';
import {PdfSaveLink} from './PdfSaveLink/PdfSaveLink';
import {OpenHousesList} from './OpenHouse/OpenHousesList';

const detailListStyle: CSSProperties = {
    paddingInlineStart: '25px',
};

function titleCheck(str: string | undefined) {
    return str ? toTitleCase(str) : '';
}

function upperCheck(str: string | undefined) {
    return str ? str?.toUpperCase() : '';
}

function pricePerFoot(price: any, area: number | undefined) {
    if (price && area && area > 0) {
        const value = Math.round(price / area);
        return value;
    } else {
        return 0;
    }
}

function PropertyDetails() {
    const [descriptionOpen, setDescriptionOpen] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [openGallery, setOpenGallery] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(true);
    const [property, setProperty] = useState<Property>();
    const [matchedCountyObject, setMatchedCountyObject] = useState<any>(false);

    const userOnMobile = useRecoilValue(AppState.gsIsMobile);

    const urlPath: LocationState = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [fetchSingleProperty] = useGetProperties();

    useEffect(() => {
        const propertyId = getPropertyIdFromUrl(urlPath);
        if (propertyId) {
            fetchSingleProperty(propertyId)
                .then((home: Property | null) => {
                    if (home === undefined || home === null) navigate('/404');
                    setProperty(home as Property);
                })
                .catch((error: any) => {
                    console.error(`Problem getting property data for: ${propertyId}`, error);
                    navigate('/404');
                });
        } else {
            const propertyAddress = getPropertyAddressFromUrl(urlPath);
            if (propertyAddress) {
                fetchSinglePropertyViaAddress(propertyAddress)
                    .then((home: Property | null) => {
                        if (home === undefined || home === null) navigate('/404');
                        setProperty(home as Property);
                    })
                    .catch((error: any) => {
                        console.error(`Problem getting property data for: ${propertyId}`, error);
                        navigate('/404');
                    });
            } else {
                console.error('Cannot get property information for: ', propertyId);
            }
        }
    }, [urlPath]);

    async function getMatchedCounty(mlsCountyName: string, mlsStateName: string) {
        const countyMatch = await getMatchedCountyObject(mlsCountyName, mlsStateName);
        if (countyMatch) {
            setMatchedCountyObject(countyMatch);
        }
    }

    useEffect(() => {
        if (property && property.CountyOrParish && property.StateOrProvince) {
            getMatchedCounty(property.CountyOrParish, property.StateOrProvince);
        }
    }, [property?.CountyOrParish, property?.StateOrProvince]);

    function handleGalleryOpen(order: any) {
        setOpenGallery(!openGallery);
        setSelectedImage(order);
    }

    function handleCounterClick() {
        setOpenGallery(true);
        setSelectedImage(1);
    }

    function calculateDaysOnMarket(onMarketDate: Date | undefined, closeDate: Date | undefined) {
        if (onMarketDate === undefined) return null;

        let nowOrClosingDate;

        closeDate === undefined ? (nowOrClosingDate = new Date()) : (nowOrClosingDate = new Date(closeDate));

        const listingDate = new Date(onMarketDate);

        const millisecondsPerHour = 1000 * 60 * 60;
        const millisecondsPerDay = millisecondsPerHour * 24;

        const millisecondsSinceListing = nowOrClosingDate.getTime() - listingDate.getTime();
        const hoursSinceListing = millisecondsSinceListing / millisecondsPerHour;
        const daysSinceListing = millisecondsSinceListing / millisecondsPerDay;
        const hoursSinceListingRounded = Math.round(hoursSinceListing);
        const daysSinceListingRounded = Math.floor(daysSinceListing);

        if (hoursSinceListingRounded <= 0) return 'less than 1 day'; // captures negative numbers for the instances when somehow CloseDate is earlier than OnMarketDate
        if (hoursSinceListingRounded < 1) return '< 1 hour';
        if (hoursSinceListingRounded === 1) return '1 hour';
        if (hoursSinceListingRounded < 48) return `${hoursSinceListingRounded} hours`;
        else return `${daysSinceListingRounded} days`;
    }

    const addressArray: Array<{ label: string; value: string }> = property && property.normalized_address?.parsed;
    let address: any = {};
    addressArray?.map(item => {
        address[item.label] = item.value;
    });

    if (Object.keys(address).length === 0) address = property?.UnparsedAddress;

    useEffect(() => {
        smoothscroll.polyfill();
    }, []);

    // Close gallery on ESC key press
    document.addEventListener('keydown', function (event) {
        const key = event.key;
        if (key === 'Escape') {
            event.preventDefault();
            if (openGallery) {
                setOpenGallery(false);
            }
        }
    });

    if (property) {
        let images: Array<any> = [];
        let photoCount = 0;

        if (property.Photos && property.Photos.length !== 0) {
            images = property.Photos.sort((a: any, b: any) => {
                return a.order - b.order;
            });
            photoCount = property.Photos.length;
        }

        const propertyAddress = address
            ? `${titleCheck(property.StreetNumber)}${
                property.StreetDirPrefix ? ' ' + property.StreetDirPrefix : ''
            } ${titleCheck(property.StreetName)} ${titleCheck(property.StreetSuffix)}${
                property.StreetDirSuffix ? ' ' + property.StreetDirSuffix : ''
            }${property.UnitNumber ? ' Unit ' + property.UnitNumber : ''}, ${property.City ? titleCheck(property.City) : property.PostalCity ? titleCheck(property.PostalCity) : ''}, ${upperCheck(
                property.StateOrProvince,
            )} ${titleCheck(property.PostalCode)}`
            : '';

        const ListPrice = Number(property.ListPrice);

        const apiQueryParams = {
            price: ListPrice,
            downDollar: ListPrice * 0.2,
            loanDollar: ListPrice - ListPrice * 0.2,
            FirstTimeHomeBuyer: false,
            existingLoanBalance: '',
            originalLoanAmount: '',
            purchasedWithinLastYear: false,
            rate: '0',
            loanProgram: 'Conventional',
            loanPurpose: 'Purchase',
            loanTerm: '30-Year Term',
            loanType: 'Fixed',
            propertyType: 'Single Family Residence',
            propertyUse: 'Primary Residence',
            cityAndState: `${
                matchedCountyObject && matchedCountyObject[0].CountyName
                    ? matchedCountyObject[0].CountyName
                    : property.CountyOrParish
            }, ${
                matchedCountyObject && matchedCountyObject[0].State ? matchedCountyObject[0].State : property.StateOrProvince
            }, USA`,
            countyId: matchedCountyObject && matchedCountyObject[0].CountyId ? matchedCountyObject[0].CountyId : 123,
            countyRealty: matchedCountyObject ? (matchedCountyObject[0]?.IsRealtyApplied ? 'Yes' : 'No') : 'No',
            needRealtyTeam: matchedCountyObject ? (matchedCountyObject[0]?.IsRealtyApplied ? 'Yes' : 'No') : 'No',
            baseRate: '',
            points: 0,
            loanAmountType: 'Conforming',
            refiOff: false,
            isCashOutRefinanceDisabled: '',
            homeownersInsuranceEscrow: 0,
            propertyTax: 0,
        };

        const apiUrl = (appUrl: string) => {
            return encodeURI(
                `${baseUrl}${appUrl}?` +
        `quoteWidget[price]=${apiQueryParams.price}` +
        `&quoteWidget[downDollar]=${apiQueryParams.downDollar}` +
        `&quoteWidget[loanDollar]=${apiQueryParams.loanDollar}` +
        `&quoteWidget[FirstTimeHomeBuyer]=${apiQueryParams.FirstTimeHomeBuyer}` +
        `&quoteWidget[existingLoanBalance]=${apiQueryParams.existingLoanBalance}` +
        `&quoteWidget[originalLoanAmount]=${apiQueryParams.originalLoanAmount}` +
        `&quoteWidget[purchasedWithinLastYear]=${apiQueryParams.purchasedWithinLastYear}` +
        `&quoteWidget[rate]=${apiQueryParams.rate}` +
        `&quoteWidget[LoanProgram]=${apiQueryParams.loanProgram}` +
        `&quoteWidget[LoanPurpose]=${apiQueryParams.loanPurpose}` +
        `&quoteWidget[LoanTerm]=${apiQueryParams.loanTerm}` +
        `&quoteWidget[LoanType]=${apiQueryParams.loanType}` +
        `&quoteWidget[PropertyType]=${apiQueryParams.propertyType}` +
        `&quoteWidget[PropertyUse]=${apiQueryParams.propertyUse}` +
        `&quoteWidget[address]=${apiQueryParams.cityAndState}` +
        `&quoteWidget[PropertyLocation]=${apiQueryParams.cityAndState}` +
        `&quoteWidget[CountyId]=${apiQueryParams.countyId}` +
        `&quoteWidget[CountyRealty]=${apiQueryParams.countyRealty}` +
        `&quoteWidget[NeedRealtyTeam]=${apiQueryParams.needRealtyTeam}` +
        `&quoteWidget[BaseRate]=${apiQueryParams.baseRate}` +
        `&quoteWidget[Points]=${apiQueryParams.points}` +
        `&quoteWidget[LoanAmountType]=${apiQueryParams.loanAmountType}` +
        `&quoteWidget[refiOff]=${apiQueryParams.refiOff}` +
        `&quoteWidget[isCashOutRefinanceDisabled]=${apiQueryParams.isCashOutRefinanceDisabled}` +
        `&quoteWidget[HomeownersInsuranceEscrow]=${apiQueryParams.homeownersInsuranceEscrow}` +
        `&quoteWidget[PropertyTax]=${apiQueryParams.propertyTax}`,
            );
        };

        const ctaButtonStyle = {
            top: userOnMobile ? 0 : 48,
        };

        const addressText = (property.StreetNumber ? `${property.StreetNumber} ` : '') +
      (property.StreetDirPrefix ? `${property.StreetDirPrefix}. ` : '') +
      (property.StreetName ? `${titleCheck(property.StreetName)} ` : '') +
      (property.StreetSuffix ? `${titleCheck(property.StreetSuffix)}` : '') +
      (property.StreetDirSuffix ? ` ${property.StreetDirSuffix}` : '') +
      (property.UnitNumber ? ` Unit ${property.UnitNumber}` : '') +
      ', ' +
      (property.City ? property.City + ', ' : property.PostalCity ? property.PostalCity + ', ' : '') +
      (property.StateOrProvince ? `${property.StateOrProvince} ` : '') +
      (property.PostalCode ? `${property.PostalCode} ` : '');

        const metaTitle =
      addressText +
      `| MLS ${property.ListingId}`;

        const meta = {
            title: metaTitle,
            description: `Real estate listing details for ${metaTitle}`,
            siteName: 'CapCenter',
            image: images?.[0]?.url,
        };

        return (
            <React.Fragment>
                <Helmet>
                    <title className="text-capitalize">{meta.title}</title>
                    <meta itemProp="name" content={meta.title}/>
                    <meta itemProp="description" content={meta.description}/>
                    <meta itemProp="image" content={meta.image}/>

                    {/* Facebook */}
                    <meta name="description" content={meta.description}/>
                    <meta property="og:title" content={meta.title}/>
                    <meta property="og:site_name" content={meta.siteName}/>
                    <meta property="og:description" content={meta.description}/>
                    <meta property="og:url" content={'https://www.capcenterhomes.com/homes' + buildPropertyDetailsUrl(property)}/>
                    <meta property="og:image" content={meta.image}/>

                    {/* TWITTER */}
                    <meta name="twitter:card" content="summary_large_image"/>
                    <meta name="twitter:site" content="@CapCenterLLC"/>
                    <meta name="twitter:title" content={meta.siteName}/>
                    <meta name="twitter:description" content={meta.description}/>
                    <meta name="twitter:image" content={meta.image}/>

                    {property ? (
                        <link rel="canonical" href={'https://www.capcenterhomes.com/homes' + buildPropertyDetailsUrl(property)}/>
                    ) : (
                        <></>
                    )}
                </Helmet>
                <div id="propertydetails" className="property-details bg-white w-100 h-100">
                    <PropertyDetailsHeader
                        handleCloseDetails={() => navigate(searchParams.get('prevUrl') ?? '/')}
                        bpSM={userOnMobile}
                        canonicalUrl={'https://www.capcenterhomes.com/homes' + buildPropertyDetailsUrl(property)}
                        PdfSaveElement={
                            <PdfSaveLink
                                property={property}
                                capCenter={isCapCenterListOffice(
                                    property.ListOfficeName,
                                    property.ListOfficeMlsId,
                                    property.OriginatingSystemName,
                                )}
                                onMarketDate={property.OnMarketDate || property.DerivedOnMarketTimestamp}
                                staticMapSrc={GoogleStaticMapSrc(property, 775, 436)}
                                photoCount={photoCount}
                                closeDate={property.CloseDate}
                                countyId={matchedCountyObject ? matchedCountyObject[0].CountyId : undefined}
                            />
                        }
                    />
                    <main className="position-relative">
                        <div className="row position-relative max-w-xl m-auto" id="overview">
                            <BreadCrumb
                                city={property.City ?? ''}
                                county={property.CountyOrParish ?? ''}
                                state={property.StateOrProvince ?? ''}
                                postalCode={property.PostalCode ?? ''}
                            />
                        </div>
                        <div className="row max-w-xl m-auto">
                            <div className="col-12 col-lg-8 mt-3 px-2 position-relative">
                                {/* 3-IMAGE PHOTO TILES */}
                                <div className="" id="overview">
                                    <section className="image-gallery__wrapper--details-view row g-0 w-100">
                                        {userOnMobile && (
                                            <SavingsHighlight
                                                price={property.ListPrice ? property.ListPrice : 0}
                                                isRealtyApplied={matchedCountyObject ? matchedCountyObject[0].IsRealtyApplied : false}
                                                countyId={matchedCountyObject ? matchedCountyObject[0].CountyId : undefined}
                                                savingsUrl={apiUrl(AppList.quotePurchase.url)}
                                                closeDate={property.CloseDate}
                                            />
                                        )}
                                        <ImageGallery
                                            images={images}
                                            property={property}
                                            onImageClick={handleGalleryOpen}
                                            onCounterClick={handleCounterClick}
                                            capCenter={isCapCenterListOffice(
                                                property.ListOfficeName,
                                                property.ListOfficeMlsId,
                                                property.OriginatingSystemName,
                                            )}
                                            onMarketDate={property.OnMarketDate || property.DerivedOnMarketTimestamp}
                                            standardStatus={property.StandardStatus}
                                            staticMapSrc={GoogleStaticMapSrc(property, 775, 436)}
                                            photoCount={photoCount}
                                            closeDate={property.CloseDate}
                                            addressText={meta.title}
                                        />
                                    </section>
                                </div>
                                <div className="row max-w-xl m-auto">
                                    {/* LEFT BODY COLUMN */}
                                    <section className="">
                                        <h2 className='h6'>
                      Real estate for sale in{' '}
                                            {(property.City ? `${titleCheck(property.City)}, ` : property.PostalCity ? `${titleCheck(property.PostalCity)}, ` : '') +
                      (property.StateOrProvince ? `${upperCheck(property.StateOrProvince)} ` : '') +
                      (property.PostalCode ? `${titleCheck(property.PostalCode)}` : '')}
                                        </h2>
                                        {/* PROPERTY ADDRESS */}
                                        <PropertyLocation
                                            line1={
                                                <React.Fragment>
                                                    {(property.StreetNumber ? `${property.StreetNumber} ` : '') +
                          (property.StreetDirPrefix ? `${property.StreetDirPrefix} ` : '') +
                          (property.StreetName ? `${titleCheck(property.StreetName)} ` : '') +
                          (property.StreetSuffix ? `${titleCheck(property.StreetSuffix)}` : '') +
                          (property.StreetDirSuffix ? ` ${property.StreetDirSuffix}` : '') +
                          (property.UnitNumber ? ` Unit ${property.UnitNumber}` : '')}
                                                </React.Fragment>
                                            }
                                            line2={
                                                <React.Fragment>
                                                    {(property.City ? `${titleCheck(property.City)}, ` : property.PostalCity ? `${titleCheck(property.PostalCity)}, ` : '') +
                          (property.StateOrProvince ? `${upperCheck(property.StateOrProvince)} ` : '') +
                          (property.PostalCode ? `${titleCheck(property.PostalCode)}` : '')}
                                                </React.Fragment>
                                            }
                                        />

                                        {/* HIGHLIGHTED DETAILS */}
                                        <DetailsHighlight
                                            savingsUrl={apiUrl(AppList.quotePurchase.url)}
                                            isRealtyApplied={matchedCountyObject ? matchedCountyObject[0].IsRealtyApplied : false}
                                            countyId={matchedCountyObject ? matchedCountyObject[0].CountyId : undefined}
                                            price={property.ClosePrice ? property.ClosePrice : property.ListPrice ? property.ListPrice : 0}
                                            bedrooms={property.BedroomsTotal}
                                            bathroomsTotal={property.RawMlsBathroomsTotal}
                                            bathroomsFull={property.BathroomsFull}
                                            bathroomsHalf={property.BathroomsHalf}
                                            area={property.LivingArea ? numeral(property.LivingArea).format() : '--'}
                                            closeDate={property.CloseDate}
                                        />

                                        {/* MOBILE CTA BUTTONS  */}
                                        {userOnMobile && (
                                            <CallsToActionHighlight
                                                address={propertyAddress}
                                                price={property.ClosePrice ? property.ClosePrice : property.ListPrice ? property.ListPrice : 0}
                                                applyNowUrl={getUrlWithUtmQueryString(apiUrl(AppList.applyNow.url)) ?? ''}
                                                calculatorUrl={getUrlWithUtmQueryString(apiUrl(AppList.quotePurchase.url)) ?? ''}
                                                closeDate={property.CloseDate}
                                                openHouses={property.OpenHouse}
                                            />
                                        )}

                                        {userOnMobile && <div className="row g-0 w-100 align-items-center my-4">
                                            <article className="col-12 my-1 text-center">
                                                <a
                                                    href={getUrlWithUtmQueryString(apiUrl(AppList.quotePurchase.url))}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                    className="text-decoration-underline fw-bold py-2"
                                                >
                                                    <h2 className='h6'>Estimate monthly payments and closing costs</h2>
                                                </a>
                                            </article>
                                        </div>}

                                        {userOnMobile && property.OpenHouse &&
                    <OpenHousesList OpenHouses={property.OpenHouse} address={propertyAddress}/>}


                                        {/* DESCRIPTION */}
                                        <ExpansionComponent
                                            id="propertyDescription"
                                            className="row g-0 my-4"
                                            openLabel="See less"
                                            closedLabel="See more"
                                            openIcon="fas fa-chevron-up"
                                            closedIcon="fas fa-chevron-down"
                                            bpSM={userOnMobile}
                                            isOpen={descriptionOpen}
                                            onButtonClick={() => setDescriptionOpen(!descriptionOpen)}
                                            content={
                                                <PropertyDescription
                                                    description={property.PublicRemarks}
                                                    addressText={metaTitle}
                                                    property={property}
                                                />
                                            }
                                        />

                                        <section id="listingDetails" className="mt-3">
                                            {/* IMPORTANT DETAILS */}
                                            <ImportantDetails
                                                status={property.MlsStatus}
                                                listingDate={property.OnMarketDate || property.DerivedOnMarketTimestamp}
                                                time={calculateDaysOnMarket(
                                                    (property.StandardStatus === 'Active' ? property.StatusChangeTimestamp : undefined) ||
                          property.OnMarketTimestamp || property.DerivedOnMarketTimestamp,
                                                    property.CloseDate,
                                                )}
                                                propertyType={property.PropertyType}
                                                price={numeral(property.ListPrice?.toString()).format()}
                                                pricePerFoot={pricePerFoot(
                                                    (property.StandardStatus !== 'Active' ? property.ClosePrice : property.ListPrice) ??
                          property.ListPrice,
                                                    property.LivingArea,
                                                )}
                                                livingArea={property.LivingArea}
                                                lotSizeAcres={property.LotSizeAcres}
                                                yearBuilt={property.YearBuilt}
                                                county={property.CountyOrParish}
                                                community={property.SubdivisionName}
                                                associationYN={property.AssociationFee !== undefined}
                                                associationFee={property.AssociationFee}
                                                associationFeeFrequency={property.AssociationFeeFrequency}
                                                closeDate={property.CloseDate}
                                                closePrice={numeral(property.ClosePrice?.toString()).format()}
                                            />

                                            <ExpansionComponent
                                                id="fullDetails"
                                                openLabel="See less details"
                                                closedLabel="See more details"
                                                openIcon="fas fa-chevron-up"
                                                closedIcon="fas fa-chevron-down"
                                                buttonClass="my-1"
                                                bpSM={userOnMobile}
                                                isOpen={detailsOpen}
                                                onButtonClick={() => setDetailsOpen(!detailsOpen)}
                                                content={
                                                    <FullDetails
                                                        detailListStyle={detailListStyle}
                                                        status={property.MlsStatus}
                                                        time={property.DaysOnMarket}
                                                        propertyType={property.PropertyType}
                                                        community={property.SubdivisionName}
                                                        lotSizeAcres={property.LotSizeAcres}
                                                        yearBuilt={property.YearBuilt}
                                                        associationYN={property.AssociationYN}
                                                        associationFee={property.AssociationFee}
                                                        bathroomsFull={property.BathroomsFull}
                                                        bathroomsHalf={property.BathroomsHalf}
                                                        roomsTotal={property.RoomsTotal}
                                                        bedroomsTotal={property.BedroomsTotal}
                                                        appliances={property.Appliances}
                                                        interiorFeatures={property.InteriorFeatures}
                                                        fireplaceYN={property.FireplaceYN}
                                                        fireplaceFeatures={property.FireplaceFeatures}
                                                        fireplacesTotal={property.FireplacesTotal}
                                                        basement={property.Basement}
                                                        heating={property.Heating}
                                                        cooling={property.Cooling}
                                                        elementary={property.ElementarySchool}
                                                        middle={property.MiddleOrJuniorSchool}
                                                        high={property.HighSchool}
                                                        subdivisionName={property.SubdivisionName}
                                                        zoningDescription={property.Zoning}
                                                        sewer={property.Sewer}
                                                        water={property.WaterSource}
                                                        parkingFeatures={property.ParkingFeatures}
                                                        garageYN={property.GarageYN}
                                                        attachedGarageYN={property.AttachedGarageYN}
                                                        carportYN={property.CarportYN}
                                                        carportSpaces={property.CarportSpaces}
                                                        archStyle={property.ArchitecturalStyle}
                                                        accessibilityFeatures={property.AccessibilityFeatures}
                                                        stories={property.Stories}
                                                        storiesTotal={property.StoriesTotal}
                                                        flooring={property.Flooring}
                                                        roof={property.Roof}
                                                        constructionMaterials={property.ConstructionMaterials}
                                                        exteriorFeatures={property.ExteriorFeatures}
                                                        fencing={property.Fencing}
                                                        poolPrivateYN={property.PoolPrivateYN}
                                                        poolFeatures={property.PoolFeatures}
                                                        lotSizeArea={property.LotSizeArea}
                                                        livingArea={property.LivingArea}
                                                        ownership={property.Ownership}
                                                        ownershipType={property.OwnershipType}
                                                        taxAssessedValue={property.TaxAssessedValue}
                                                        annualTax={property.TaxAnnualAmount}
                                                        taxYear={property.TaxYear}
                                                        parcelNumber={property.ParcelNumber}
                                                        mlsCode={property.OriginatingSystemName}
                                                    />
                                                }
                                            />

                                            <ListingDetails
                                                property={property}
                                                detailListStyle={detailListStyle}
                                                listAgent={property.ListAgentFullName}
                                                listOfficeName={property.ListOfficeName}
                                                listOfficeMlsId={property.ListOfficeMlsId}
                                                service={property.ListingService}
                                                agreement={property.ListingAgreement}
                                                mlsListingId={property.ListingId}
                                                mlsSystem={property.OriginatingSystemName}
                                                mlsStatus={property.MlsStatus}
                                                buyerOfficeMlsId={property.BuyerOfficeMlsId}
                                                originatingSystemName={property.OriginatingSystemName}
                                            />

                                            <article className="mt-4 mt-lg-0" style={{marginBottom: '-0.5rem'}}>
                                                <a
                                                    href={`${baseRealEstateUrl}${AppList.disclosures.url}`}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                    className="cc-color--teal-500 text-decoration-underline"
                                                >
                          *View disclosures
                                                </a>
                                            </article>

                                            <article className="mb-4">
                                                <MlsDisclosures
                                                    mlsSource={property.OriginatingSystemName}
                                                    lastSyncTimestamp={property.lastSyncTimestamp}
                                                    listingStatus={property.StandardStatus}
                                                />
                                            </article>

                                            {/* Uncomment the next line to print out the property's JSON */}
                                            {/* <pre className="mt-5 pt-5">{JSON.stringify(property, null, 4)}</pre> */}
                                        </section>
                                    </section>

                                </div>
                            </div>
                            <div className="col-12 col-lg-4 mt-3 px-2 position-relative">
                                {/* RIGHT COLUMN */}
                                <div
                                    className={classNames('h-100', userOnMobile ? 'position-relative' : 'fixed')}
                                    style={ctaButtonStyle}
                                >
                                    {
                                        userOnMobile ? <></> :
                                            <div className='h-100'>
                                                <span style={{paddingBottom: 10, width: '100%'}}>
                                                    <SavingsHighlight
                                                        savingsUrl={apiUrl(AppList.quotePurchase.url)}
                                                        price={
                                                            property.ClosePrice ? property.ClosePrice : property.ListPrice ? property.ListPrice : 0
                                                        }
                                                        isRealtyApplied={matchedCountyObject ? matchedCountyObject[0].IsRealtyApplied : false}
                                                        countyId={matchedCountyObject ? matchedCountyObject[0].CountyId : undefined}
                                                        closeDate={property.CloseDate}
                                                    />
                                                </span>
                                                <CallsToActionHighlight
                                                    address={propertyAddress}
                                                    price={
                                                        property.ClosePrice ? property.ClosePrice : property.ListPrice ? property.ListPrice : 0
                                                    }
                                                    applyNowUrl={getUrlWithUtmQueryString(apiUrl(AppList.applyNow.url)) ?? ''}
                                                    calculatorUrl={getUrlWithUtmQueryString(apiUrl(AppList.quotePurchase.url)) ?? ''}
                                                    closeDate={property.CloseDate}
                                                    openHouses={property.OpenHouse}
                                                />
                                            </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </main>
                    <Footer light/>
                </div>

                {openGallery && (
                    <FullScreenGallery
                        open={openGallery}
                        closeBtnClick={() => setOpenGallery(false)}
                        property={property}
                        selectedImageId={selectedImage}
                        setSelectedImageId={setSelectedImage}
                        staticMapSrc={GoogleStaticMapSrc(property, 775, 436)}
                        bpSM={userOnMobile}
                        photoCount={photoCount}
                        addressText={addressText}
                    />
                )}
            </React.Fragment>
        );
    } else {
        return <PropertyDetailsSkeleton/>;
    }
}

export default PropertyDetails;
